import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Landmark, LockKeyhole, RefreshCw } from 'lucide-react';
import { finalizeDailyClosing, loadDailyClosingData } from './data.js';
import { dateLabel, friendlyError, jakartaDateKey, number, rupiah } from './lib/format.js';

const METHODS = [
  { key: 'cash', label: 'Tunai' },
  { key: 'qris', label: 'QRIS' },
  { key: 'bank_transfer', label: 'Transfer bank' },
  { key: 'debit_card', label: 'Kartu debit' },
  { key: 'credit_card', label: 'Kartu kredit' },
  { key: 'other', label: 'Lainnya' }
];

function amount(value) {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function initialActual(data) {
  const saved = data?.existing_closing?.snapshot_json?.actual;
  const source = saved || data?.system || {};
  return Object.fromEntries(METHODS.map(({ key }) => [key, String(amount(source[key]))]));
}

export function DailyClosingPage({ identity, refreshKey }) {
  const [closingDate, setClosingDate] = useState(jakartaDateKey());
  const [data, setData] = useState(null);
  const [actual, setActual] = useState(() => Object.fromEntries(METHODS.map(({ key }) => [key, '0'])));
  const [varianceReason, setVarianceReason] = useState('');
  const [notes, setNotes] = useState('');
  const [acknowledgeOpen, setAcknowledgeOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const canWrite = identity.permissions.has('billing.write');
  const today = jakartaDateKey();

  async function load(date = closingDate) {
    setLoading(true);
    setError('');
    try {
      const next = await loadDailyClosingData(date);
      setData(next);
      setActual(initialActual(next));
      setVarianceReason(next.existing_closing?.snapshot_json?.variance_reason || '');
      setNotes(next.existing_closing?.notes || '');
      setAcknowledgeOpen(Boolean(next.existing_closing?.snapshot_json?.acknowledge_open_items));
    } catch (loadError) {
      setError(friendlyError(loadError));
      setData(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(closingDate);
    // refreshKey berasal dari tombol Muat ulang global.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closingDate, refreshKey]);

  const locked = /^(FINAL|CLOSED)$/i.test(data?.existing_closing?.status || '');
  const closingSnapshot = data?.existing_closing?.snapshot_json || {};
  const system = useMemo(
    () => data?.existing_closing?.snapshot_json?.system || data?.system || {},
    [data]
  );
  const openInvoiceCount = locked
    ? Number(closingSnapshot.open_invoice_count ?? data?.open_invoice_count ?? 0)
    : Number(data?.open_invoice_count || 0);
  const openInvoiceAmount = locked
    ? amount(closingSnapshot.open_invoice_amount ?? data?.open_invoice_amount)
    : amount(data?.open_invoice_amount);
  const openInvoices = locked
    ? (closingSnapshot.open_invoices || data?.open_invoices || [])
    : (data?.open_invoices || []);
  const differences = useMemo(() => Object.fromEntries(
    METHODS.map(({ key }) => [key, amount(actual[key]) - amount(system[key])])
  ), [actual, system]);
  const actualTotal = useMemo(
    () => METHODS.reduce((sum, { key }) => sum + amount(actual[key]), 0),
    [actual]
  );
  const systemTotal = amount(system.total_receipts);
  const totalDifference = actualTotal - systemTotal;
  const hasDifference = METHODS.some(({ key }) => differences[key] !== 0);

  function updateActual(key, value) {
    if (value === '' || /^\d+(\.\d{0,2})?$/.test(value)) {
      setActual((current) => ({ ...current, [key]: value }));
    }
  }

  async function finalize() {
    setError('');
    setNotice('');
    if (METHODS.some(({ key }) => actual[key] === '' || amount(actual[key]) < 0)) {
      setError('Semua nilai aktual wajib diisi dengan angka nol atau lebih.');
      return;
    }
    if (hasDifference && varianceReason.trim().length < 10) {
      setError('Ada selisih penerimaan. Jelaskan alasannya minimal 10 karakter.');
      return;
    }
    if (openInvoiceCount > 0 && !acknowledgeOpen) {
      setError('Periksa daftar tagihan terbuka dan centang konfirmasi sebelum menutup kas.');
      return;
    }
    const question = hasDifference
      ? `Finalisasi tutup kas ${dateLabel(closingDate)} dengan selisih ${rupiah.format(totalDifference)}?`
      : `Finalisasi tutup kas ${dateLabel(closingDate)} tanpa selisih?`;
    if (!window.confirm(`${question}\n\nTutup kas final tidak dapat diulang.`)) return;

    setBusy(true);
    try {
      const saved = await finalizeDailyClosing(closingDate, {
        actual: Object.fromEntries(METHODS.map(({ key }) => [key, amount(actual[key])])),
        variance_reason: varianceReason.trim() || null,
        notes: notes.trim() || null,
        acknowledge_open_items: acknowledgeOpen
      });
      setData(saved);
      setActual(initialActual(saved));
      setNotice(`Tutup kas ${saved.existing_closing?.closing_number || closingDate} berhasil difinalisasi.`);
    } catch (saveError) {
      setError(friendlyError(saveError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-stack closing-page">
      <section className="page-heading">
        <div>
          <span className="eyebrow">Rekonsiliasi penerimaan XV</span>
          <h2>Tutup Kas Harian</h2>
          <p>Bandingkan penerimaan sistem dengan hasil pemeriksaan kasir sebelum finalisasi.</p>
        </div>
        <div className="closing-date-tools">
          <label className="form-field">
            Tanggal
            <input
              type="date"
              value={closingDate}
              max={today}
              onChange={(event) => setClosingDate(event.target.value)}
            />
          </label>
          <button className="button button-quiet" disabled={loading} onClick={() => load()}>
            <RefreshCw size={17} className={loading ? 'spin' : ''} /> Perbarui
          </button>
        </div>
      </section>

      {error ? <div className="alert alert-danger">{error}</div> : null}
      {notice ? <div className="alert alert-success"><strong>Berhasil.</strong> {notice}</div> : null}
      {loading ? <div className="content-loading"><div className="loader" /><span>Menghitung penerimaan harian…</span></div> : null}

      {!loading && data ? (
        <>
          {locked ? (
            <div className="alert alert-success closing-lock-banner">
              <LockKeyhole size={19} />
              <span>
                Kas telah ditutup sebagai <strong>{data.existing_closing.closing_number}</strong>
                {' '}pada {dateLabel(data.existing_closing.closed_at, { time: true })}.
              </span>
            </div>
          ) : null}

          <section className="closing-metrics">
            <div className="metric">
              <div className="metric-icon"><Landmark size={21} /></div>
              <div><span>Penerimaan sistem</span><strong>{rupiah.format(systemTotal)}</strong><small>{number.format(system.paid_transaction_count || 0)} transaksi lunas</small></div>
            </div>
            <div className="metric metric-green">
              <div className="metric-icon"><CheckCircle2 size={21} /></div>
              <div><span>Total aktual</span><strong>{rupiah.format(actualTotal)}</strong><small>Hasil verifikasi kasir</small></div>
            </div>
            <div className={`metric ${totalDifference !== 0 ? 'metric-warn' : 'metric-green'}`}>
              <div className="metric-icon"><AlertTriangle size={21} /></div>
              <div><span>Selisih</span><strong>{rupiah.format(totalDifference)}</strong><small>Aktual dikurangi sistem</small></div>
            </div>
            <div className="metric">
              <div className="metric-icon"><Landmark size={21} /></div>
              <div><span>Transaksi paket</span><strong>{number.format(system.package_transaction_count || 0)}</strong><small>Tanpa penerimaan kas</small></div>
            </div>
          </section>

          <section className="panel closing-reconciliation">
            <div className="panel-head">
              <div><span className="eyebrow">Per metode pembayaran</span><h3>Rekonsiliasi penerimaan</h3></div>
              <span className="muted">Selisih = aktual − sistem</span>
            </div>
            <div className="closing-method-head">
              <span>Metode</span><span>Sistem</span><span>Aktual</span><span>Selisih</span>
            </div>
            {METHODS.map(({ key, label }) => (
              <div className="closing-method-row" key={key}>
                <strong>{label}</strong>
                <span>{rupiah.format(amount(system[key]))}</span>
                {locked ? (
                  <span>{rupiah.format(amount(actual[key]))}</span>
                ) : (
                  <div className="closing-amount-input"><span>Rp</span><input inputMode="decimal" value={actual[key]} onChange={(event) => updateActual(key, event.target.value)} /></div>
                )}
                <strong className={differences[key] !== 0 ? 'danger-text' : ''}>{rupiah.format(differences[key])}</strong>
              </div>
            ))}

            <div className="form-grid closing-notes">
              <label className="form-field">
                Alasan selisih {hasDifference ? '*' : ''}
                <textarea rows="3" value={varianceReason} disabled={locked} onChange={(event) => setVarianceReason(event.target.value)} placeholder="Wajib bila ada selisih per metode pembayaran." />
                <small>Minimal 10 karakter bila salah satu metode berbeda.</small>
              </label>
              <label className="form-field">
                Catatan tutup kas
                <textarea rows="3" value={notes} disabled={locked} onChange={(event) => setNotes(event.target.value)} placeholder="Catatan operasional tambahan bila diperlukan." />
              </label>
            </div>
          </section>

          <section className={`panel ${openInvoiceCount > 0 ? 'panel-alert' : ''}`}>
            <div className="panel-head">
              <div><span className="eyebrow">Pemeriksaan sebelum final</span><h3>Tagihan terbuka tanggal ini</h3></div>
              <strong>{number.format(openInvoiceCount)} · {rupiah.format(openInvoiceAmount)}</strong>
            </div>
            {openInvoiceCount === 0 ? (
              <div className="closing-clear"><CheckCircle2 size={20} /> Tidak ada tagihan terbuka pada tanggal ini.</div>
            ) : (
              <>
                <div className="table-wrap closing-open-table">
                  <table>
                    <thead><tr><th>Invoice</th><th>Pasien</th><th>Status</th><th>Tagihan</th></tr></thead>
                    <tbody>{openInvoices.map((row) => (
                      <tr key={row.id}><td><strong>{row.invoice_number}</strong></td><td>{row.client_name}</td><td>{row.payment_status}</td><td>{rupiah.format(amount(row.net_amount))}</td></tr>
                    ))}</tbody>
                  </table>
                </div>
                <label className="closing-acknowledge">
                  <input type="checkbox" checked={acknowledgeOpen} disabled={locked} onChange={(event) => setAcknowledgeOpen(event.target.checked)} />
                  <span>Saya sudah memeriksa tagihan terbuka. Tagihan tetap tersimpan dan dapat diselesaikan kasir setelah tutup kas.</span>
                </label>
              </>
            )}
          </section>

          <section className="closing-operational">
            <div><span>Kunjungan</span><strong>{number.format(data.operational?.total_visits || 0)}</strong></div>
            <div><span>Tindakan lunas</span><strong>{number.format(data.operational?.total_actions || 0)}</strong></div>
            <div><span>Kunjungan belum selesai</span><strong>{number.format(data.operational?.open_visit_count || 0)}</strong></div>
          </section>

          {!locked && canWrite ? (
            <div className="closing-finalize">
              <div><strong>Siap melakukan tutup kas?</strong><span>Angka sistem dihitung ulang oleh server saat finalisasi.</span></div>
              <button className="button button-primary" disabled={busy} onClick={finalize}>
                <LockKeyhole size={18} /> {busy ? 'Memfinalisasi…' : 'Finalisasi Tutup Kas'}
              </button>
            </div>
          ) : null}
          {!locked && !canWrite ? <div className="alert alert-info">Akun ini dapat melihat laporan, tetapi tidak mempunyai izin menutup kas.</div> : null}

          <section className="panel table-panel">
            <div className="table-summary"><strong>{number.format(data.history?.length || 0)}</strong><span>riwayat tutup kas terbaru</span></div>
            <div className="table-wrap closing-history">
              <table>
                <thead><tr><th>Tanggal</th><th>Nomor</th><th>Transaksi</th><th>Penerimaan</th><th>Selisih</th><th>Ditutup oleh</th><th>Status</th></tr></thead>
                <tbody>{(data.history || []).map((row) => (
                  <tr key={row.id}>
                    <td>{dateLabel(row.closing_date)}</td><td><strong>{row.closing_number}</strong></td>
                    <td>{number.format(row.total_transactions || 0)}</td><td>{rupiah.format(amount(row.total_revenue))}</td>
                    <td className={amount(row.difference?.total_receipts) !== 0 ? 'danger-text' : ''}>{rupiah.format(amount(row.difference?.total_receipts))}</td>
                    <td>{row.closed_by_name || '—'}</td><td>{row.status}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
